import React, {useEffect, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {makeRequired, makeValidate} from "mui-rff";
import * as Yup from "yup";
import Dialog from "../../base/dialog";
import {DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import Form from "../../base/form";
import TryAgain from "../../app-specific/try-again";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import WarrantyAndReturnExtraFeesTitleCard from "../../app-specific/part-support/part-support-setting-cards/extra-fee-title-card";

const schema = Yup.object().shape({
    newTitle: Yup.string().nullable().required()
})

const subscription = {submitting: true, values: true, initialValues: true};


const WarrantyAndReturnExtraFeesTitleManagementModal = ({setTitles: setTitlesProps, open, setOpen}) => {
    const [titles, setTitles] = useState([])
    const [loading, setLoading] = useState(false)
    const [changed, setChanged] = useState(false)
    const [creating, setCreating] = useState(false)
    const validate = makeValidate(schema)
    const required = makeRequired(schema)
    const isMounted = useIsMounted()

    /**
     * Listens for the changes in open state of the model and with each change:
     *
     * if the modal is closing, and the titles have changed, then syncs the inner titles with titleProps
     * if the modal is opening, fetches the list of titles from the server.
     */
    useEffect(() => {
        if (!open && changed) {
            setTitlesProps(titles)
            setChanged(false)
        } else {
            getTitles();
        }
    }, [open])

    /**
     * Fetches the titles of the extra fees from the server and sets the inner list.
     */
    const getTitles = () => {
        setLoading(true)
        Api.getPartSupportRequestExtraFeesTitles().then((response) => {
            if (!isMounted()) return;
            setTitles(response?.data?.map(e => ({...e, editing: false})) ?? [])
            setLoading(false)
        })
    }

    /**
     * Create a new title with the given values.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createTitle = async (values, form) => {
        setCreating(true)
        const forApi = {
            title: values.newTitle,
        }
        const response = await Api.createPartSupportRequestExtraFeesTitle(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setTitles(prevState => [
                response.data,
                ...prevState
            ])
            setChanged(true)
            setTimeout(() => form.restart({}), 0)
        }
        setCreating(false)
        return false
    }

    /**
     * Updates a title with the given values.
     * @param {any} title the Extra fees title to be updated.
     * @return {Promise<boolean>}
     */
    const updateTitle = async (title) => {
        setTitles(prevState => prevState.map(e => e.id === title.id ? {
            ...e,
            editing: true
        } : e))
        const forApi = {
            ...title,
        }
        const response = await Api.editPartSupportRequestExtraFeesTitle(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setTitles(prevState => prevState?.map(e => e.id === title.id
                ? {...e, ...title}
                : e))
            setChanged(true)
        }
        setTitles(prevState => prevState?.map(e => e.id === title.id
            ? {...e, editing: false}
            : e))
        return response?.resultFlag ?? false
    }

    /**
     * Removes a title from the server and then removes it from the local list.
     * @param {any} title the title to be removed
     */
    const removeTitle = (title) => {
        setTitles(prevState => prevState.map(e => e.id === title.id ? {
            ...e,
            editing: true
        } : e))
        Api.removePartSupportRequestExtraFeesTitle(title.id).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setTitles(prevState => prevState?.filter(e => e.id !== title.id))
                setChanged(true)
            } else {
                setTitles(prevState => prevState?.map(e => e.id === title.id
                    ? {...e, editing: false}
                    : e))
            }
        })
    }

    return (
        <>
            <Dialog
                maxWidth={'md'}
                open={open}
                setOpen={setOpen}
                loading={creating || loading}
            >
                <DialogTitle className={'pb-0'}>
                    <Row xs={12} className='no-gutters justify-content-center w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            Extra Fees Title Management
                        </p>
                    </Row>
                </DialogTitle>
                <DialogContent>
                    <Divider className='divider'/>
                    <Col xs={12}>
                        <p className={'text-md font-weight-bold mt-4 mb-2'}>
                            Create a new Title
                        </p>
                    </Col>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-center'
                        onSubmit={createTitle}
                        subscription={subscription}
                        validate={validate}
                        initialValues={{}}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col className={'pl-3'}>
                                        <OutlinedInputForm
                                            name={'newTitle'}
                                            placeholder={'title'}
                                            label={"New Title"}
                                            required={required.newTitle}
                                            value={values.newTitle ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={'auto'}>
                                        <Button
                                            className='py-2-5 px-5'
                                            type={'submit'}
                                            variant={'contained'}
                                            color={'primary'}
                                            disabled={creating}
                                        >
                                            {
                                                creating
                                                    ? "Adding..."
                                                    : "Add"
                                            }
                                        </Button>
                                    </Col>
                                </>
                            )
                        }}
                    />
                    <div className='my-2 w-100'/>
                    <Col xs={12} className={'mt-3 d-flex flex-wrap'}>
                        {
                            loading
                                ? Array(4).fill(null).map((e, i) => (
                                    <div key={i} className={'loading normal w-100 mb-3'}>
                                        <div/>
                                    </div>
                                ))
                                : titles?.length < 1
                                    ? <TryAgain
                                        text={"There is currently no title submitted. you can add one" +
                                            " using the form above."}
                                    />
                                    : <TransitionGroup className={'d-flex flex-wrap w-100'}>
                                        {
                                            titles?.map(title => (
                                                <CSSTransition
                                                    key={title.id}
                                                    classNames='item'
                                                    timeout={500}
                                                >
                                                    <WarrantyAndReturnExtraFeesTitleCard
                                                        title={title}
                                                        update={updateTitle}
                                                        remove={removeTitle}
                                                    />
                                                </CSSTransition>
                                            ))
                                        }
                                    </TransitionGroup>
                        }
                    </Col>
                </DialogContent>
            </Dialog>
        </>
    )
}


export default WarrantyAndReturnExtraFeesTitleManagementModal;
